import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Navigation } from '../components/Navigation';
import InfoTooltip from '../components/InfoTooltip';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, ReferenceLine } from 'recharts';
import api from '../utils/api';

// Format number with Danish thousand separator
const formatDKK = (num) => {
  if (num === null || num === undefined) return '0';
  return Math.round(num).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
};

// Weekly Spend Chart Component
const WeeklySpendChart = ({ data }) => {
  if (!data || data.length === 0) {
    return (
      <div className="h-[250px] flex items-center justify-center bg-white/5 rounded-lg">
        <p className="text-sm text-white/40">Indlæser data...</p>
      </div>
    );
  }

  const maxSpend = Math.max(...data.map(d => d.spend));
  const yAxisMax = Math.ceil(maxSpend / 50000) * 50000;

  return (
    <ResponsiveContainer width="100%" height={250}>
      <BarChart data={data}>
        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
        <XAxis 
          dataKey="week_number" 
          stroke="rgba(255,255,255,0.4)" 
          tick={{ fill: 'rgba(255,255,255,0.6)', fontSize: 10 }}
          tickFormatter={(v) => `${v}`}
        />
        <YAxis 
          stroke="rgba(255,255,255,0.4)" 
          tick={{ fill: 'rgba(255,255,255,0.6)', fontSize: 10 }}
          domain={[0, yAxisMax]}
          tickFormatter={(v) => v >= 1000 ? `${(v/1000).toFixed(0)}k` : v}
          width={50}
        />
        <Tooltip 
          contentStyle={{ backgroundColor: '#1A1A1A', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px' }}
          labelStyle={{ color: '#ffffff' }}
          itemStyle={{ color: '#ffffff' }}
          formatter={(value) => {
            const formattedValue = Math.round(value).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
            return [`${formattedValue} kr`, 'Marketing spend'];
          }}
          labelFormatter={(label, payload) => {
            if (payload && payload[0]) {
              return `Uge ${payload[0].payload.week_number}`;
            }
            return `Uge ${label}`;
          }}
          cursor={{ fill: 'rgba(255,255,255,0.1)' }}
        />
        <Bar 
          dataKey="spend" 
          fill="#1E6F7A"
          radius={[4, 4, 0, 0]}
        />
      </BarChart>
    </ResponsiveContainer>
  );
};

// Weekly New Bookings Chart Component
const WeeklyNewBookingsChart = ({ data }) => {
  if (!data || data.length === 0) {
    return (
      <div className="h-[250px] flex items-center justify-center bg-white/5 rounded-lg">
        <p className="text-sm text-white/40">Indlæser data...</p>
      </div>
    );
  }

  const maxBookings = Math.max(...data.map(d => d.new_bookings));
  const yAxisMax = Math.ceil(maxBookings / 50) * 50;

  return (
    <ResponsiveContainer width="100%" height={250}>
      <BarChart data={data}>
        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
        <XAxis 
          dataKey="week_number" 
          stroke="rgba(255,255,255,0.4)" 
          tick={{ fill: 'rgba(255,255,255,0.6)', fontSize: 10 }}
          tickFormatter={(v) => `${v}`}
        />
        <YAxis 
          stroke="rgba(255,255,255,0.4)" 
          tick={{ fill: 'rgba(255,255,255,0.6)', fontSize: 10 }}
          domain={[0, yAxisMax]}
          width={40}
        />
        <Tooltip 
          contentStyle={{ backgroundColor: '#1A1A1A', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px' }}
          labelStyle={{ color: '#ffffff' }}
          itemStyle={{ color: '#ffffff' }}
          formatter={(value) => {
            return [value, 'Nye bookinger'];
          }}
          labelFormatter={(label, payload) => {
            if (payload && payload[0]) {
              return `Uge ${payload[0].payload.week_number}`;
            }
            return `Uge ${label}`;
          }}
          cursor={{ fill: 'rgba(255,255,255,0.1)' }}
        />
        <Bar 
          dataKey="new_bookings" 
          fill="#2E7D32"
          radius={[4, 4, 0, 0]}
        />
      </BarChart>
    </ResponsiveContainer>
  );
};

// Weekly Cost Per Booking Chart Component
const WeeklyCostPerBookingChart = ({ data }) => {
  if (!data || data.length === 0) {
    return (
      <div className="h-[250px] flex items-center justify-center bg-white/5 rounded-lg">
        <p className="text-sm text-white/40">Indlæser data...</p>
      </div>
    );
  }

  const validData = data.filter(d => d.cost_per_booking !== null);
  const maxCost = validData.length > 0 ? Math.max(...validData.map(d => d.cost_per_booking)) : 2000;
  const yAxisMax = Math.ceil(maxCost / 500) * 500;

  return (
    <ResponsiveContainer width="100%" height={250}>
      <BarChart data={data}>
        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
        <XAxis 
          dataKey="week_number" 
          stroke="rgba(255,255,255,0.4)" 
          tick={{ fill: 'rgba(255,255,255,0.6)', fontSize: 10 }}
          tickFormatter={(v) => `${v}`}
        />
        <YAxis 
          stroke="rgba(255,255,255,0.4)" 
          tick={{ fill: 'rgba(255,255,255,0.6)', fontSize: 10 }}
          domain={[0, yAxisMax]}
          tickFormatter={(v) => v >= 1000 ? `${(v/1000).toFixed(1)}k` : v}
          width={45}
        />
        <Tooltip 
          contentStyle={{ backgroundColor: '#1A1A1A', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px' }}
          labelStyle={{ color: '#ffffff' }}
          itemStyle={{ color: '#ffffff' }}
          formatter={(value, name, props) => {
            if (value === null) return ['Ingen data', ''];
            const formattedValue = Math.round(value).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
            return [`${formattedValue} kr`, 'Pris pr. booking'];
          }}
          labelFormatter={(label, payload) => {
            if (payload && payload[0]) {
              const data = payload[0].payload;
              return `Uge ${data.week_number} (${data.new_bookings} bookinger)`;
            }
            return `Uge ${label}`;
          }}
          cursor={{ fill: 'rgba(255,255,255,0.1)' }}
        />
        <Bar 
          dataKey="cost_per_booking" 
          radius={[4, 4, 0, 0]}
        >
          {data.map((entry, index) => {
            if (entry.cost_per_booking === null) return <Cell key={`cell-${index}`} fill="transparent" />;
            let color = '#2E7D32'; // Green - good (≤ 1200)
            if (entry.cost_per_booking > 1500) color = '#B3261E'; // Red - expensive
            else if (entry.cost_per_booking > 1200) color = '#C77700'; // Orange - warning
            return <Cell key={`cell-${index}`} fill={color} />;
          })}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
};

// Weekly Profit Per Opmåling Chart Component
const WeeklyProfitPerOpmalingChart = ({ data }) => {
  if (!data || data.length === 0) {
    return (
      <div className="h-[250px] flex items-center justify-center bg-white/5 rounded-lg">
        <p className="text-sm text-white/40">Indlæser data...</p>
      </div>
    );
  }

  const validData = data.filter(d => d.profit_per_opmaling !== null && d.opmaalinger > 0);
  const profits = validData.map(d => d.profit_per_opmaling);
  const minProfit = profits.length > 0 ? Math.min(...profits) : 0;
  const maxProfit = profits.length > 0 ? Math.max(...profits) : 3000;
  
  // Calculate Y-axis domain to show both positive and negative values
  const yMin = Math.min(minProfit, 0);
  const yMax = Math.max(maxProfit, 0);
  const padding = Math.abs(yMax - yMin) * 0.1;
  const yAxisMin = Math.floor((yMin - padding) / 500) * 500;
  const yAxisMax = Math.ceil((yMax + padding) / 500) * 500;

  return (
    <ResponsiveContainer width="100%" height={250}>
      <BarChart data={data}>
        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
        <XAxis 
          dataKey="week_number" 
          stroke="rgba(255,255,255,0.4)" 
          tick={{ fill: 'rgba(255,255,255,0.6)', fontSize: 10 }}
          tickFormatter={(v) => `${v}`}
        />
        <YAxis 
          stroke="rgba(255,255,255,0.4)" 
          tick={{ fill: 'rgba(255,255,255,0.6)', fontSize: 10 }}
          domain={[yAxisMin, yAxisMax]}
          tickFormatter={(v) => v >= 1000 ? `${(v/1000).toFixed(1)}k` : v < -1000 ? `${(v/1000).toFixed(1)}k` : v}
          width={50}
        />
        <Tooltip 
          contentStyle={{ backgroundColor: '#1A1A1A', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px' }}
          labelStyle={{ color: '#ffffff' }}
          itemStyle={{ color: '#ffffff' }}
          formatter={(value, name, props) => {
            if (value === null || props.payload.opmaalinger === 0) return ['Ingen data', ''];
            const formattedValue = Math.round(value).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
            return [`${formattedValue} kr`, 'Profit pr. opmåling'];
          }}
          labelFormatter={(label, payload) => {
            if (payload && payload[0]) {
              const d = payload[0].payload;
              return `Uge ${d.week_number} (${d.opmaalinger} opmålinger)`;
            }
            return `Uge ${label}`;
          }}
          cursor={{ fill: 'rgba(255,255,255,0.1)' }}
        />
        <ReferenceLine y={0} stroke="rgba(255,255,255,0.3)" strokeWidth={1} />
        <Bar 
          dataKey="profit_per_opmaling" 
          radius={[4, 4, 0, 0]}
        >
          {data.map((entry, index) => {
            if (entry.opmaalinger === 0) return <Cell key={`cell-${index}`} fill="transparent" />;
            let color = '#2E7D32'; // Green - profitable (≥ 2000)
            if (entry.profit_per_opmaling < 0) color = '#B3261E'; // Red - loss
            else if (entry.profit_per_opmaling < 1500) color = '#C77700'; // Orange - low profit
            return <Cell key={`cell-${index}`} fill={color} />;
          })}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
};

const SpendAnalyse = () => {
  const navigate = useNavigate();
  const [region, setRegion] = useState('Hele landet');
  const regions = ['Hele landet', 'Sjælland', 'Fyn', 'Jylland'];
  
  const [weeklySpendData, setWeeklySpendData] = useState(null);
  const [weeklyNewBookingsData, setWeeklyNewBookingsData] = useState(null);
  const [weeklyCostPerBookingData, setWeeklyCostPerBookingData] = useState(null);
  const [weeklyProfitabilityData, setWeeklyProfitabilityData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const regionParam = region !== 'Hele landet' ? `?region=${encodeURIComponent(region)}` : '';
      
      const [weeklySpendRes, weeklyNewBookingsRes, weeklyCostRes, weeklyProfitRes] = await Promise.all([
        api.get(`/spend/weekly-history${regionParam}`),
        api.get(`/bookings/weekly-new-bookings${regionParam}`),
        api.get(`/bookings/weekly-cost-per-booking${regionParam}`),
        api.get(`/marketing/profitability-weekly${regionParam}`)
      ]);
      
      setWeeklySpendData(weeklySpendRes.data);
      setWeeklyNewBookingsData(weeklyNewBookingsRes.data);
      setWeeklyCostPerBookingData(weeklyCostRes.data);
      setWeeklyProfitabilityData(weeklyProfitRes.data);
    } catch (err) {
      console.error('Data fetch error:', err);
      if (err.response?.status === 401) {
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  }, [region, navigate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return (
    <div className="min-h-screen bg-[#1A1A1A]">
      {/* Header */}
      <header className="bg-[#1A1A1A] border-b border-white/10 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Navigation />
            <h1 className="text-xl font-semibold text-white">Spend Analyse</h1>
          </div>
          <div className="flex items-center gap-3">
            <select
              value={region}
              onChange={(e) => setRegion(e.target.value)}
              className="bg-[#1A1A1A] border border-white/10 text-white text-sm px-4 py-2 rounded-lg focus:outline-none focus:border-[#1E6F7A]"
              data-testid="region-filter"
            >
              {regions.map((r) => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
          </div>
        </div>
      </header>
      
      <main className="p-6">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-white/60">Indlæser data...</div>
          </div>
        ) : (
          <div className="space-y-6">
            {/* First row - 2 charts side by side */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Marketing Spend pr. uge */}
              <div className="bg-white/5 rounded-xl p-5">
                <div className="flex items-center gap-2 mb-4">
                  <h2 className="text-lg font-semibold text-white">Marketing Spend pr. uge</h2>
                  <InfoTooltip
                    title="Marketing Spend pr. uge"
                    description="Viser den samlede marketing spend for hver uge de seneste 12 uger."
                    formula="Samlet spend = Sum af dagligt spend (Facebook + Google + andre kanaler)"
                    details={[
                      "Data hentes fra Import_Spend_pr._dag sheet",
                      "Kan filtreres pr. region (Sjælland, Fyn, Jylland)",
                      "Inkluderer alle marketingkanaler"
                    ]}
                    dataSource="Import_Spend _pr._dag"
                  />
                </div>
                <WeeklySpendChart data={weeklySpendData?.weekly_data} />
                {weeklySpendData?.weekly_data && weeklySpendData.weekly_data.length > 0 && (
                  <div className="flex justify-between items-center mt-4 pt-4 border-t border-white/10">
                    <div>
                      <p className="text-xs text-white/60">Gns. pr. uge</p>
                      <p className="text-lg font-bold text-[#1E6F7A]">
                        {formatDKK(weeklySpendData.weekly_data.reduce((sum, w) => sum + w.spend, 0) / weeklySpendData.weekly_data.length)} kr
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-white/60">Samlet (12 uger)</p>
                      <p className="text-lg font-bold text-white">
                        {formatDKK(weeklySpendData.weekly_data.reduce((sum, w) => sum + w.spend, 0))} kr
                      </p>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Nye bookinger pr. uge */}
              <div className="bg-white/5 rounded-xl p-5">
                <div className="flex items-center gap-2 mb-4">
                  <h2 className="text-lg font-semibold text-white">Nye bookinger pr. uge</h2>
                  <InfoTooltip
                    title="Nye bookinger pr. uge"
                    description="Viser antal nye bookinger oprettet i hver uge de seneste 12 uger."
                    formula="Nye bookinger = Sum af opmaling_bookings_created pr. uge"
                    details={[
                      "Tæller bookinger baseret på oprettelsesdato",
                      "Ikke at forveksle med afviklede bookinger",
                      "Bruges til at måle marketing effektivitet"
                    ]}
                    dataSource="CalendarTypeCounts (opmaling_bookings_created)"
                  />
                </div>
                <WeeklyNewBookingsChart data={weeklyNewBookingsData?.weekly_data} />
                {weeklyNewBookingsData?.weekly_data && weeklyNewBookingsData.weekly_data.length > 0 && (
                  <div className="flex justify-between items-center mt-4 pt-4 border-t border-white/10">
                    <div>
                      <p className="text-xs text-white/60">Gns. pr. uge</p>
                      <p className="text-lg font-bold text-[#2E7D32]">
                        {Math.round(weeklyNewBookingsData.weekly_data.reduce((sum, w) => sum + w.new_bookings, 0) / weeklyNewBookingsData.weekly_data.length)}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-white/60">Samlet (12 uger)</p>
                      <p className="text-lg font-bold text-white">
                        {weeklyNewBookingsData.weekly_data.reduce((sum, w) => sum + w.new_bookings, 0)}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            {/* Second row - 2 charts side by side */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Pris pr. booking pr. uge */}
              <div className="bg-white/5 rounded-xl p-5">
                <div className="flex items-center gap-2 mb-4">
                  <h2 className="text-lg font-semibold text-white">Pris pr. booking pr. uge</h2>
                  <InfoTooltip
                    title="Pris pr. booking pr. uge"
                    description="Viser den gennemsnitlige pris for at opnå én ny booking baseret på marketing spend."
                    formula="Pris pr. booking = Marketing spend ÷ Antal nye bookinger"
                    details={[
                      "Grøn: ≤ 1.200 kr (godt)",
                      "Orange: 1.200-1.500 kr (acceptabelt)",
                      "Rød: > 1.500 kr (for dyrt)",
                      "Lavere er bedre - målet er under 1.200 kr"
                    ]}
                    dataSource="Beregnet fra spend og bookinger"
                  />
                </div>
                <WeeklyCostPerBookingChart data={weeklyCostPerBookingData?.weekly_data} />
                {weeklyCostPerBookingData?.weekly_data && (
                  <div className="flex justify-between items-center mt-4 pt-4 border-t border-white/10">
                    <div>
                      <p className="text-xs text-white/60">Gennemsnitlig pris pr. booking</p>
                      <p className="text-lg font-bold" style={{ 
                        color: weeklyCostPerBookingData.avg_cost_per_booking <= 1200 ? '#2E7D32' : 
                               weeklyCostPerBookingData.avg_cost_per_booking <= 1500 ? '#C77700' : '#B3261E' 
                      }}>
                        {formatDKK(weeklyCostPerBookingData.avg_cost_per_booking)} kr
                      </p>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Profit pr. opmåling pr. uge */}
              <div className="bg-white/5 rounded-xl p-5">
                <div className="flex items-center gap-2 mb-4">
                  <h2 className="text-lg font-semibold text-white">Profit pr. opmåling pr. uge</h2>
                  <InfoTooltip
                    title="Profit pr. opmåling pr. uge"
                    description="Viser nettofortjenesten pr. opmåling efter alle omkostninger. Dette er den reelle profit fra hver booking."
                    formula="Profit = (Dækningsbidrag - 20% konsulent provision - Marketing spend) ÷ Antal opmålinger"
                    details={[
                      "Grøn: ≥ 2.000 kr (god profit)",
                      "Orange: 0-2.000 kr (lav profit)",
                      "Rød: Negativ (tab)",
                      "Dækningsbidrag hentes fra ordrer i Data_klar",
                      "20% konsulent provision fratrækkes automatisk"
                    ]}
                    dataSource="Data_klar_1, Data_klar, Import_Spend _pr._dag"
                  />
                </div>
                <WeeklyProfitPerOpmalingChart data={weeklyProfitabilityData?.weekly_data} />
                {weeklyProfitabilityData?.weekly_data && weeklyProfitabilityData.weekly_data.length > 0 && (
                  <div className="flex justify-between items-center mt-4 pt-4 border-t border-white/10">
                    <div>
                      <p className="text-xs text-white/60">Gns. profit pr. opmåling</p>
                      {(() => {
                        const validWeeks = weeklyProfitabilityData.weekly_data.filter(w => w.opmaalinger > 0);
                        const avgProfit = validWeeks.length > 0 
                          ? validWeeks.reduce((sum, w) => sum + w.profit_per_opmaling, 0) / validWeeks.length 
                          : 0;
                        return (
                          <p className="text-lg font-bold" style={{ 
                            color: avgProfit >= 2000 ? '#2E7D32' : avgProfit >= 0 ? '#C77700' : '#B3261E' 
                          }}>
                            {formatDKK(avgProfit)} kr
                          </p>
                        );
                      })()}
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-white/60">Samlet (10 uger)</p>
                      <p className="text-lg font-bold text-white">
                        {formatDKK(weeklyProfitabilityData.weekly_data.reduce((sum, w) => sum + w.net_profit, 0))} kr
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default SpendAnalyse;
